import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';

export type ScreenshotStatus = 'none' | 'pending' | 'ready' | 'failed';

export type PreviewWebsiteDto = {
  /** Effective address (override wins over auto-resolution); "" -> skeleton. */
  url: string;
  source: string | null;
  is_override: boolean;
  screenshot_url: string | null;
  /** Vertical mobile-viewport screenshot for the phone-frame preview. */
  screenshot_mobile_url: string | null;
  screenshot_status: ScreenshotStatus;
  screenshot_taken_at: string | null;
};

// Stop the spinner AND the poll after this long; a stuck-pending row (worker down) must not poll forever.
const MAX_CAPTURING_MS = 90_000;
const POLL_INTERVAL_MS = 2_500;
// Back off when the GET itself errors so a backend blip isn't hammered at poll rate.
const POLL_ERROR_INTERVAL_MS = 10_000;

/** AI Preview website (resolved/edited address + Firecrawl screenshot); polls while capturing. */
export const usePreviewWebsite = () => {
  const { t } = useTranslation('ai_preview_page');
  const queryClient = useQueryClient();

  // attempt bumps on each edit/refresh so a re-capture restarts the spinner and poll even if already pending.
  const [attempt, setAttempt] = useState(0);
  const [capturingExpired, setCapturingExpired] = useState(false);

  const query = useQuery<PreviewWebsiteDto>({
    queryKey: [QueryKey.previewWebsite.get],
    refetchInterval: (q) => {
      if (q.state.data?.screenshot_status !== 'pending' || capturingExpired) return false;
      return q.state.fetchFailureCount > 0 ? POLL_ERROR_INTERVAL_MS : POLL_INTERVAL_MS;
    },
  });

  const status = query.data?.screenshot_status;

  useEffect(() => {
    if (status !== 'pending') {
      setCapturingExpired(false);
      return;
    }
    setCapturingExpired(false);
    const timer = setTimeout(() => setCapturingExpired(true), MAX_CAPTURING_MS);
    return () => clearTimeout(timer);
  }, [status, attempt]);

  // Cancel in-flight polls so a response that left before the mutation can't overwrite its fresher cache write.
  const onMutate = async () => {
    setAttempt((a) => a + 1);
    await queryClient.cancelQueries({ queryKey: [QueryKey.previewWebsite.get] });
  };
  const onError = () => toast.error(t('preview_error'));

  const setUrl = useMutation<PreviewWebsiteDto, Error, { url: string }>({
    mutationKey: [QueryKey.previewWebsite.get],
    onMutate,
    onError,
  });

  const refresh = useMutation<PreviewWebsiteDto, Error, void>({
    mutationKey: [QueryKey.previewWebsite.refresh],
    onMutate,
    onError,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isCapturing: status === 'pending' && !capturingExpired,
    // Failed outright, or stuck pending past the give-up window (worker down).
    didFail: status === 'failed' || (status === 'pending' && capturingExpired),
    setUrl,
    refresh,
  };
};
